import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserIcon, CalendarIcon, TitleIcon } from '../constants.tsx';
import type { Post } from '../App.tsx';

const BlogPostPage: React.FC<{ postId: string; posts: Post[] }> = ({ postId, posts }) => {
    const { t } = useTranslation();

    const post = posts.find(p => p.id === postId);

    if (!post) {
        return (
             <div className="pt-32 md:pt-40 xl:pt-44 pb-20 text-center">
                <h2 className="text-white text-3xl font-bold">{t('postNotFound')}</h2>
                <a href="#/blog" className="text-purple-400 hover:text-purple-300 mt-4 inline-block">{t('backToBlog')}</a>
             </div>
        )
    }

    const recommendedPosts = posts
        .filter(p => p.id !== postId)
        .sort(() => 0.5 - Math.random()) // Shuffle
        .slice(0, 3);
        
    return (
        <div className="pt-32 md:pt-40 xl:pt-44 pb-20">
            <div className="max-w-4xl mx-auto px-4 sm:px-8 xl:px-0">
                <article>
                    <div className="mb-8">
                        <h1 className="text-white font-extrabold text-3xl md:text-5xl mb-4 leading-tight">{post.title}</h1>
                        <div className="flex items-center gap-6 flex-wrap text-sm text-white/60">
                            <div className="flex items-center gap-2 flex-wrap">
                                <UserIcon />
                                <span>{post.author}</span>
                                {post.authorTitle && <span className="text-white/40">· {post.authorTitle}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <CalendarIcon />
                                <span>{post.date}</span>
                            </div>
                        </div>
                    </div>
                    
                    <img src={post.imageUrl} alt={post.title} className="w-full rounded-2xl mb-8 shadow-lg" />
                    
                    <div className="prose prose-invert prose-lg max-w-none text-white/80 leading-relaxed space-y-6">
                        {post.content.split('\\n').map((paragraph, index) => (
                           <p key={index}>{paragraph}</p>
                        ))}
                    </div>
                </article>

                {recommendedPosts.length > 0 && (
                    <aside className="mt-20 pt-10 border-t border-white/10">
                        <h3 className="text-white font-bold text-2xl md:text-3xl mb-8 text-center">{t('recommendedPosts')}</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                           {recommendedPosts.map(recPost => (
                               <div key={recPost.id} className="group">
                                    <div className="mb-4 overflow-hidden rounded-xl">
                                        <a href={recPost.href || `#/blog/${recPost.id}`}>
                                            <img src={recPost.imageUrl} alt={recPost.title} className="w-full h-48 object-cover ease-in-out duration-500 scale-100 group-hover:scale-110" />
                                        </a>
                                    </div>
                                    <h4 className="text-start">
                                        <a href={recPost.href || `#/blog/${recPost.id}`} className="text-white font-semibold text-lg ease-in duration-300 hover:opacity-80 line-clamp-2">{recPost.title}</a>
                                    </h4>
                                    <p className="text-sm text-white/60 mt-2">{recPost.date}</p>
                               </div>
                           ))}
                        </div>
                    </aside>
                )}
            </div>
        </div>
    );
};

export default BlogPostPage;
